import { QUALITY_INTERVALS } from "@/lib/music/theory-helpers";

export type ParsedChord = {
  root: string;
  /** A key matching `QUALITY_INTERVALS` so callers can derive intervals. */
  quality: string;
};

/**
 * Maps a suffix from an analyzer chord symbol to a `QUALITY_INTERVALS`
 * key. The order matters, longer matches must be tested first so
 * "maj7" doesn't get parsed as "" + "aj7".
 */
const SUFFIX_RULES: ReadonlyArray<readonly [RegExp, string]> = [
  [/^maj7\b/, "maj7"],
  [/^M7\b/, "maj7"],
  [/^m7b5\b/, "m7b5"],
  [/^min7\b/, "m7"],
  [/^m7\b/, "m7"],
  [/^minmaj7\b/, "minmaj7"],
  [/^dim7\b/, "dim7"],
  [/^dim\b/, "diminished"],
  [/^aug7\b/, "aug7"],
  [/^aug\b/, "augmented"],
  [/^sus2\b/, "sus2"],
  [/^sus4\b/, "sus4"],
  [/^7sus4\b/, "7sus4"],
  [/^maj6\b/, "maj6"],
  [/^min6\b/, "m6"],
  [/^m6\b/, "m6"],
  [/^maj9\b/, "maj9"],
  [/^m9\b/, "m9"],
  [/^add9\b/, "add9"],
  [/^min\b/, "minor"],
  [/^m\b/, "minor"],
  [/^M\b/, "major"],
  [/^7\b/, "7"],
  [/^9\b/, "9"],
  [/^6\b/, "6"],
];

/**
 * Parses an analyzer chord symbol (e.g. `"Am"`, `"F#m7"`, `"C/E"`) into
 * `{ root, quality }` so callers can derive pitch-classes for live
 * activation. Returns `null` for shapes we can't confidently parse so
 * the chip falls back to a neutral, non-active rendering.
 *
 * Slash chords (`"C/E"`) drop the bass, we only need pitch-classes
 * and the bass alters voicing, not the chord identity.
 */
export function parseChordSymbol(symbol: string): ParsedChord | null {
  if (!symbol || typeof symbol !== "string") {
    return null;
  }

  const cleaned = symbol.split("/")[0]?.trim() ?? "";

  const match = cleaned.match(/^([A-G])([#b])?(.*)$/);

  if (!match) {
    return null;
  }

  const [, pitch, accidental, suffix] = match;
  const root = `${pitch}${accidental ?? ""}`;
  const trimmedSuffix = suffix.trim();

  if (trimmedSuffix === "") {
    return { root, quality: "major" };
  }

  for (const [pattern, quality] of SUFFIX_RULES) {
    if (pattern.test(trimmedSuffix)) {
      return { root, quality };
    }
  }

  // Unknown suffix shape, caller will treat as inactive but still
  // displayable. We deliberately don't fall back to "major" here, as
  // that would lie about the chord identity for unusual suffixes.
  if (QUALITY_INTERVALS[trimmedSuffix]) {
    return { root, quality: trimmedSuffix };
  }

  return null;
}
